import { useEffect, useMemo } from 'react';
import { useActiveClassroom, type SubPageKey } from '../context/ActiveClassroomProvider.js';
import { useClassroomPages } from '../hooks/useClassroomPages.js';
import styles from './LivePageNav.module.css';

/**
 * Live-mode page-flip controls. Renders left + right glass chevrons
 * pinned to the screen edges and binds ArrowLeft / ArrowRight to the
 * same nav sequence so a teacher with a clicker, keyboard, or just a
 * tap on either edge can step through the morning-meeting pages.
 *
 * Sequence: Home + the per-classroom enabled pages, in the order set
 * on the General tab. Search is intentionally skipped — it only opens
 * via the magnifier button on MmPageNav. From Search, the arrows go
 * straight to the prev/next page in the enabled list.
 */
export function LivePageNav() {
  const { active, subPage, goTo } = useActiveClassroom();
  const { enabledPages } = useClassroomPages(active?.id);

  const sequence: SubPageKey[] = useMemo(() => {
    if (!active) return [];
    const enabled = enabledPages as SubPageKey[];
    // Always lead with Home so it's the natural first stop, even if
    // the teacher's saved order somehow lacks it.
    return enabled.includes('home') ? enabled : (['home', ...enabled] as SubPageKey[]);
  }, [active, enabledPages]);

  // Effective index: when on Search, treat the cursor as being at the
  // last non-search page so arrows feel intuitive.
  const effectiveIndex = useMemo(() => {
    if (subPage === 'search') return -1;
    return sequence.indexOf(subPage);
  }, [subPage, sequence]);

  const canPrev = sequence.length > 0 && (subPage === 'search' || effectiveIndex > 0);
  const canNext =
    sequence.length > 0 &&
    (subPage === 'search' || (effectiveIndex >= 0 && effectiveIndex < sequence.length - 1));

  const goPrev = () => {
    if (sequence.length === 0) return;
    if (subPage === 'search') {
      // Exit Search to its first page.
      goTo(sequence[0]!);
      return;
    }
    if (effectiveIndex > 0) goTo(sequence[effectiveIndex - 1]!);
  };

  const goNext = () => {
    if (sequence.length === 0) return;
    if (subPage === 'search') {
      goTo(sequence[0]!);
      return;
    }
    if (effectiveIndex >= 0 && effectiveIndex < sequence.length - 1) {
      goTo(sequence[effectiveIndex + 1]!);
    }
  };

  // Bind keyboard arrows globally while a classroom is active.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      // Don't hijack arrow keys while the teacher is typing in a field
      // (e.g. the live Search input, or any modal text input).
      const target = e.target as globalThis.HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, subPage, sequence]);

  if (!active || sequence.length <= 1) return null;

  return (
    <>
      <button
        type="button"
        className={`${styles.button} ${styles.prev}`}
        onClick={goPrev}
        disabled={!canPrev}
        aria-label="Previous page"
        data-testid="live-prev-page"
      >
        <Chevron direction="left" />
      </button>
      <button
        type="button"
        className={`${styles.button} ${styles.next}`}
        onClick={goNext}
        disabled={!canNext}
        aria-label="Next page"
        data-testid="live-next-page"
      >
        <Chevron direction="right" />
      </button>
    </>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  // Single SVG, rotated 180° when pointing left.
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={direction === 'left' ? { transform: 'scaleX(-1)' } : undefined}
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
